import { useCallback, useMemo, useRef, useState } from "react";
import QRCode from "qrcode-svg";
import { createConsumer } from "@rails/actioncable";
import Navbar from "../shared/Navbar";
import Button from "../shared/Button";
import Tag from "../shared/Tag";
import src from "./images/bagels.webp";
import Alert from "../shared/Alert";
import PromiseResolver from "../shared/PromiseResolver";
import RadioAndLabel from "../shared/RadioAndLabel";
import ListGroupItem from "../shared/ListGroupItem";
import { useMutation } from "@tanstack/react-query";
import LabelAndInput from "../shared/LabelAndInput";
import {
  CHAINAGNOSTIC,
  FEDNOW,
  MODERN_BAGELRY_ADDRESS,
  PAYMENTLINK,
  SEPOLIA_USDC_ADDRESS,
} from "../shared/constants";

export default function Root() {
  const [purchaseType, setPurchaseType] = useState<string>();
  const [purchasing, setPurchasing] = useState(false);
  const [content, setContent] = useState<string>();
  const [alertMessage, setAlertMessage] = useState<string>();
  const [paymentMethod, setPaymentMethod] = useState<string>();
  const dialogRef = useRef<HTMLDialogElement>(null);
  const paymentLinkRef = useRef<HTMLInputElement>(null);

  const createPaymentRequest = useMutation({
    onSuccess() {
      setAlertMessage(
        "Success. Please complete the checkout process in your wallet application.",
      );
      dialogRef.current?.close("payment_links");
    },
    mutationFn: async (formData: FormData) => {
      const headers = new Headers();

      const csrfTokenTag = document.querySelector("meta[name='csrf-token']");
      if (csrfTokenTag instanceof HTMLMetaElement) {
        headers.set("X-CSRF-Token", csrfTokenTag.content);
      }

      const response = await fetch("/payment_messages/create_internal", {
        method: "POST",
        headers,
        body: formData,
      });
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status.toString()}`);
      }
      return response;
    },
  });

  const onClick = useCallback(() => {
    setPurchasing(true);

    const virtualAccountPromise = new PromiseResolver<string>();
    const subscriptionPromise = new PromiseResolver<void>();

    const consumer = createConsumer();
    const subscription = consumer.subscriptions.create(
      { channel: "PaymentChannel" },
      {
        received(response: Record<string, string | undefined>) {
          if (response.virtual_account) {
            virtualAccountPromise.resolve(response.virtual_account);
          } else if (response.end_to_end_id) {
            setAlertMessage(
              `Thank you for your purchase, we have received payment ${response.end_to_end_id}.`,
            );
            dialogRef.current?.close("qr_code");
          }
        },
      },
    );

    const onClose = () => {
      setPurchasing(false);
      setPurchaseType(undefined);
      setPaymentMethod(undefined);
      createPaymentRequest.reset();

      if (
        dialogRef.current?.returnValue === "cancel" ||
        dialogRef.current?.returnValue === ""
      ) {
        subscriptionPromise.reject();
      } else {
        subscriptionPromise.resolve();
      }

      subscription.unsubscribe();
      consumer.disconnect();
    };

    const handlePayment = async () => {
      const virtualAccount = await virtualAccountPromise.promise;

      const code = Tag.create([
        ["00", "01"],
        ["01", "12"],
        [
          "26",
          [
            ["00", FEDNOW],
            ["01", "071000301"],
            ["02", virtualAccount],
          ],
        ],
        [
          "27",
          [
            ["00", PAYMENTLINK],
            ["01", "modernbagelry@modernwallet.app"],
          ],
        ],
        [
          "28",
          [
            ["00", CHAINAGNOSTIC],
            ["10", `eip155:11155111:${MODERN_BAGELRY_ADDRESS}`],
          ],
        ],
        ["52", "5462"],
        ["53", "840"],
        ["54", "100.00"],
        ["58", "US"],
        ["59", "Modern Bagelry"],
        ["60", "San Francisco"],
        [
          "62",
          [
            ["09", "AE"],
            [
              "50",
              [
                ["00", CHAINAGNOSTIC],
                ["19", `eip155:11155111/erc20:${SEPOLIA_USDC_ADDRESS}`],
              ],
            ],
          ],
        ],
      ]);

      setContent(code);

      dialogRef.current?.addEventListener("close", onClose);
      dialogRef.current?.showModal();

      try {
        await subscriptionPromise.promise;
      } catch (e) {
        if (dialogRef.current?.open) {
          throw e;
        }
      } finally {
        dialogRef.current?.removeEventListener("close", onClose);
      }
    };

    handlePayment().catch(() => {
      console.error(`handlePayment() failed`);
    });
  }, [createPaymentRequest]);

  const onBuy = useCallback(() => {
    setPurchaseType("buy");
    onClick();
  }, [onClick]);

  const onSubscribe = useCallback(() => {
    setPurchaseType("subscribe");
    onClick();
  }, [onClick]);

  const svg = useMemo(() => {
    if (content == null) {
      return "";
    }

    const qrcode = new QRCode({
      content,
      padding: 0,
      background: "white",
      join: true,
      ecl: "H",
    });
    return qrcode.svg();
  }, [content]);

  const onClose = useCallback(() => {
    dialogRef.current?.close("cancel");
  }, []);

  return (
    <>
      <Navbar />
      <dialog
        ref={dialogRef}
        className="tablet:max-w-md m-auto w-full bg-transparent backdrop:bg-black/50"
      >
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (paymentMethod === "payment_links") {
              const formData = new FormData(e.currentTarget);
              createPaymentRequest.mutate(formData);
            }
          }}
        >
          <input type="hidden" name="purchase_type" value={purchaseType} />
          <div className="pointer-events-auto relative flex w-full flex-col rounded-lg border border-black/20 bg-white bg-clip-padding outline-0 dark:bg-neutral-800">
            <div className="flex shrink-0 items-center justify-between border-b border-b-zinc-200 p-4 dark:border-zinc-600">
              <span className="mb-0 text-xl dark:text-zinc-200">Checkout</span>
              <button
                className="bg-close my-2 me-2 ms-auto box-content size-4 rounded-md border-0 bg-transparent bg-auto bg-center bg-no-repeat text-black opacity-50"
                aria-label="Close"
                onClick={onClose}
                type="button"
              />
            </div>
            <div className="p-4">
              <ul className="mb-0 flex flex-col divide-y rounded-md px-0">
                <ListGroupItem>
                  <RadioAndLabel
                    name="payment_method"
                    value="qr"
                    checked={paymentMethod == "qr"}
                    disabled={purchaseType == "subscribe"}
                    onChange={() => {
                      setPaymentMethod("qr");
                    }}
                  >
                    QR Code
                  </RadioAndLabel>
                  {paymentMethod == "qr" && (
                    <div
                      className="relative mx-auto flex-auto p-4"
                      dangerouslySetInnerHTML={{ __html: svg }}
                    />
                  )}
                </ListGroupItem>
                <ListGroupItem>
                  <RadioAndLabel
                    name="payment_method"
                    value="payment_links"
                    checked={paymentMethod == "payment_links"}
                    onChange={() => {
                      setPaymentMethod("payment_links");
                    }}
                  >
                    Payment Link
                  </RadioAndLabel>
                  {paymentMethod == "payment_links" && (
                    <div className="mt-2">
                      <LabelAndInput
                        ref={paymentLinkRef}
                        name="receiver"
                        invalid={createPaymentRequest.isError}
                        feedback="Please enter a valid wallet address."
                        disabled={createPaymentRequest.isPending}
                      />
                    </div>
                  )}
                </ListGroupItem>
              </ul>
            </div>
            <div className="flex shrink-0 flex-wrap items-center justify-end space-x-2 border-t border-t-zinc-200 p-3 dark:border-zinc-600">
              <Button buttonStyle="secondary" onClick={onClose} type="button">
                Close
              </Button>
              {paymentMethod == "payment_links" && (
                <Button disabled={createPaymentRequest.isPending}>
                  {createPaymentRequest.isPending ? "Requesting…" : "Request"}
                </Button>
              )}
            </div>
          </div>
        </form>
      </dialog>
      <div className="tablet:max-w-md mx-auto mt-3 w-full px-3">
        {alertMessage != null && <Alert>{alertMessage}</Alert>}
        <div className="relative flex min-w-0 flex-col break-words rounded-md border border-black/20 bg-white bg-clip-border text-black dark:border-white/20 dark:bg-neutral-800 dark:text-zinc-200">
          <img
            className="aspect-square w-full rounded-t-[calc(0.375rem-1px)]"
            alt="A dozen bagels"
            src={src}
          />
          <div className="flex-auto p-4">
            <h5 className="mb-2 text-xl">3 Dozen Bagels</h5>
            <h6 className="-mt-1 mb-2 text-neutral-800 opacity-75 dark:text-zinc-200">
              $100.00 USD
            </h6>
            <p className="mb-3">
              New York style bagels baked the way bagels were supposed to be
              made, with the highest quality all natural ingredients and lots of
              love. Modern Bagelry Bagels are proofed, boiled and bathed in cold
              water and then baked on wood boards in a revolving tray oven.
            </p>
            <div className="grid gap-2">
              <Button disabled={purchasing} onClick={onBuy}>
                {purchasing && purchaseType == "buy" ? "Buying…" : "Buy"}
              </Button>
              <Button
                disabled={purchasing}
                onClick={onSubscribe}
                buttonStyle="secondary"
              >
                {purchasing && purchaseType == "subscribe"
                  ? "Subscribing…"
                  : "Subscribe (Every Month)"}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
